import { useNavigate } from 'react-router-dom';
import { Shield, Users, Stethoscope, GraduationCap, MapPin, Coins, PenSquare, Headphones, ArrowLeft, CheckCircle2, Lock } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const ROLES = [
  {
    key: 'super_admin',
    label: 'Super Administrateur',
    icon: Shield,
    color: '#dc2626',
    bg: '#fef2f2',
    summary: "Le pilote général de la plateforme. Gère le contenu national, les utilisateurs, les paramètres et la sécurité.",
    canDo: [
      "Administrer toutes les pages, formations, articles et l'espace patients",
      "Gérer tous les membres, rôles et adhésions",
      "Superviser les paiements et reçus fiscaux de toutes les régions",
      "Configurer les paramètres globaux du site",
    ],
  },
  {
    key: 'regional',
    label: 'Responsable Régional',
    icon: MapPin,
    color: '#2563eb',
    bg: '#eff6ff',
    summary: "Anime sa région : page régionale, membres locaux, événements, et le cas échéant les reçus fiscaux.",
    canDo: [
      "Mettre à jour la page de sa région et son bureau régional",
      "Gérer les adhésions et les fiches annuaire de sa région",
      "Suivre les paiements et cotisations régionales",
      "Organiser les élections internes au bureau régional",
    ],
  },
  {
    key: 'tresorier',
    label: 'Trésorier',
    icon: Coins,
    color: '#d97706',
    bg: '#fffbeb',
    summary: "Garant du suivi financier : cotisations, paiements et émission des reçus fiscaux.",
    canDo: [
      "Consulter les paiements et cotisations",
      "Émettre et télécharger les reçus fiscaux",
      "Suivre les adhésions à régulariser",
    ],
  },
  {
    key: 'auteur',
    label: 'Auteur',
    icon: PenSquare,
    color: '#16a34a',
    bg: '#f0fdf4',
    summary: "Rédige et publie les contenus de formation : articles scientifiques, formations, billets de blog, événements.",
    canDo: [
      "Créer et modifier les formations et articles",
      "Publier des actualités et événements",
      "Gérer ses propres contributions depuis son espace",
    ],
  },
  {
    key: 'ophthalmologist',
    label: 'Ophtalmologiste',
    icon: Stethoscope,
    color: '#35AFE4',
    bg: '#eaf6fc',
    summary: "Membre praticien adhérent. Accès complet aux contenus réservés et participation à la vie de la société.",
    canDo: [
      "Accéder aux replays, publications et rapports SFO",
      "Participer au forum privé entre adhérents",
      "Gérer sa fiche annuaire (cabinet, expertises, photos)",
      "Voter aux élections nationales et régionales",
    ],
  },
  {
    key: 'intern',
    label: 'Interne',
    icon: GraduationCap,
    color: '#059669',
    bg: '#ecfdf5',
    summary: "Adhésion gratuite réservée aux internes en ophtalmologie. Accès aux contenus de formation.",
    canDo: [
      "Accéder aux articles de formation et publications",
      "Participer au forum privé",
      "Bénéficier des replays et cours en ligne",
    ],
  },
];

const RolesPermissionsPublicPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="roles-permissions-public">
      <SiteHeader />
      <SEO
        title="Rôles & Permissions"
        description="Découvrez les différents rôles d'utilisateurs de la SFOALC et leurs accès : ophtalmologistes, internes, trésorier, auteurs, responsables régionaux et administrateurs."
      />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5f8a 50%, #35AFE4 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20 relative z-10">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors" data-testid="back-btn">
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <Users size={24} className="text-white" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">Documentation</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Rôles & Permissions
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            La plateforme SFOALC distingue plusieurs profils d'utilisateurs.
            Chaque rôle dispose d'accès et de fonctionnalités adaptés à sa mission.
          </p>
        </div>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="block w-full h-[40px] relative z-10">
          <path fill="#f8f9fb" d="M0,60 L0,20 Q360,60 720,30 T1440,20 L1440,60 Z" />
        </svg>
      </section>

      {/* Roles list */}
      <section className="bg-[#f8f9fb] py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto space-y-5">
          {ROLES.map((role) => {
            const Icon = role.icon;
            return (
              <div key={role.key}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-7 hover:shadow-md transition-shadow"
                data-testid={`role-card-${role.key}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: role.bg }}>
                    <Icon size={22} style={{ color: role.color }} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {role.label}
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{role.summary}</p>
                    <ul className="space-y-1.5">
                      {role.canDo.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" style={{ color: role.color }} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Visitors / Patients */}
          <div className="bg-gradient-to-br from-pink-50 to-white rounded-2xl border border-pink-100 p-6 sm:p-7" data-testid="role-card-public">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-pink-100">
                <Users size={22} className="text-pink-600" strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  Visiteur / Patient
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Sans compte, vous pouvez consulter les pages publiques, l'annuaire des praticiens,
                  l'espace patients et déposer un message via le formulaire de contact.
                  Les contenus de formation, le forum et les fiches étendues sont réservés aux adhérents.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 text-sky-700 text-xs font-bold mb-4">
            <Lock size={13} /> Vous êtes membre du bureau ?
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Une vue détaillée des permissions techniques (lectures, écritures, accès API) est disponible dans le back-office administrateur.
          </p>
          <button onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white rounded-full text-sm font-bold transition-all hover:shadow-md"
            data-testid="contact-btn">
            Une question sur votre rôle ?
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RolesPermissionsPublicPage;
